/**
 * Speaker Binding API Service
 *
 * API client functions for speaker binding workflow including snippet extraction,
 * binding confirmation, speaker swapping, and workflow status checks.
 */

import { apiClient } from "./api";
import type {
  SpeakerSnippetsResponse,
  ConfirmBindingRequest,
  ConfirmBindingResponse,
  SwapAtTimestampRequest,
  SwapAtTimestampResponse,
  BindingWorkflowStatus,
} from "@/types/speaker-binding";

const API_PREFIX = import.meta.env.VITE_API_V1_PREFIX || "/api/v1";

/**
 * Get representative audio snippets for speaker binding wizard
 */
export async function getBindingSnippets(
  transcriptId: string
): Promise<SpeakerSnippetsResponse> {
  const response = await apiClient.get(
    `${API_PREFIX}/transcripts/${transcriptId}/binding/snippets`
  );
  return response.data;
}

/**
 * Confirm speaker binding with user-provided names and pronouns
 */
export async function confirmBinding(
  transcriptId: string,
  request: ConfirmBindingRequest
): Promise<ConfirmBindingResponse> {
  const response = await apiClient.post(
    `${API_PREFIX}/transcripts/${transcriptId}/binding/confirm`,
    request
  );
  return response.data;
}

/**
 * Swap speaker labels at a specific timestamp (for mid-recording position changes)
 */
export async function swapSpeakersAtTimestamp(
  transcriptId: string,
  request: SwapAtTimestampRequest
): Promise<SwapAtTimestampResponse> {
  const response = await apiClient.post(
    `${API_PREFIX}/transcripts/${transcriptId}/binding/swap-at`,
    request
  );
  return response.data;
}

/**
 * Check if speaker binding wizard is required
 */
export async function getWorkflowStatus(
  transcriptId: string
): Promise<BindingWorkflowStatus> {
  const response = await apiClient.get(
    `${API_PREFIX}/transcripts/${transcriptId}/binding/workflow-status`
  );
  return response.data;
}

/**
 * Response from confirm-and-analyze endpoint
 */
export interface ConfirmAndAnalyzeResponse {
  transcript_id: string;
  binding_status: string;
  /** Whether speakers are confirmed for analysis (pipeline gate) */
  speakers_confirmed_for_analysis: boolean;
  analysis_queued: boolean;
  reflection_id: string;
  message: string;
}

/**
 * Confirm speaker binding and trigger analysis pipeline continuation
 *
 * Used when pipeline is paused awaiting speaker confirmation.
 * Confirms binding and queues feedback/insights generation.
 */
export async function confirmBindingAndAnalyze(
  transcriptId: string,
  request: ConfirmBindingRequest
): Promise<ConfirmAndAnalyzeResponse> {
  const response = await apiClient.post(
    `${API_PREFIX}/transcripts/${transcriptId}/binding/confirm-and-analyze`,
    request
  );
  return response.data;
}
